import {
    downloadFile,
    request,
    requestWithMsg,
    uploadFileWithMsg,
    MsgResult,
} from './request';

// 关联数据统一创建 (Bundle 套件, 2026-08-20 设计 doc/relate-import).
// 一份 JSON/YAML 文件描述"主体 + 全部强关联子资源", 提交时强制预检 →
// 一个审批工单 → 审批通过后单事务落库 (参数行直接 published).
//   device   : 设备 + 模式 + 字段组值 + 校准 + 参数行
//   material : 材料 + SKU + 能力位 + 理化 + 贴士 + 参数行

/** 套件类型 (决定接口前缀与模板作用域参数). */
export type BundleKind = 'device' | 'material';

const BASE = '/v1/bundles';

/** 预检单条结果 (五要素: severity/code/path/msg + line/candidates/fixAt 可选). */
export interface BundleIssue {
    severity: 'error' | 'warning' | 'info';
    code: string;
    path: string;
    /** 文件导入时的源文件行号 (0/缺省 = 不可用) */
    line?: number;
    msg: string;
    candidates?: string[];
    fixAt?: string;
}

/** 预检聚合报告 (一次报全, 不 fail-fast). */
export interface BundleReport {
    ok: boolean;
    errors: BundleIssue[];
    warnings: BundleIssue[];
    infos: BundleIssue[];
    summary: Record<string, number>;
}

/** 提交/预检响应: dryRun 只有 report; 提交成功带 taskCode (审批) 或 created (本地直执行). */
export interface BundleSubmitResult {
    report: BundleReport;
    accepted?: boolean;
    needAck?: boolean;
    taskCode?: string;
    created?: number;
}

/** 套件文件上传 (dryRun=true 只预检; ackWarnings=true 确认告警后提交, D10 服务端兜底). */
export function importBundleFile(
    kind: BundleKind,
    file: File,
    opts: { dryRun?: boolean; ackWarnings?: boolean } = {},
): Promise<MsgResult<BundleSubmitResult>> {
    const qs = new URLSearchParams();
    if (opts.dryRun) qs.set('dryRun', 'true');
    if (opts.ackWarnings) qs.set('ackWarnings', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return uploadFileWithMsg<BundleSubmitResult>(
        `${BASE}/${kind}/import${suffix}`,
        file,
    );
}

/**
 * 模板下载 (blank=零值 / example=示例值; 按作用域实时生成, 与预检校验同源).
 * scope: 设备套件传 {archCode}, 材料套件传 {type1, type2?}.
 */
export function downloadBundleTemplate(
    kind: BundleKind,
    scope: Record<string, string>,
    format: 'yaml' | 'json',
    mode: 'blank' | 'example',
): Promise<void> {
    const qs = new URLSearchParams({ ...scope, format, mode });
    const scopeName = Object.values(scope).filter(Boolean).join('-') || kind;
    return downloadFile(
        `${BASE}/${kind}/import-template?${qs.toString()}`,
        `${kind}-bundle-${scopeName}-${mode}.${format}`,
    );
}

/** 设备套件 preflight: 本框架可用字段组 (含 fields)/模式字典/参数模板. */
export interface DeviceBundlePreflight {
    archCode: string;
    deviceSchemas: {
        groupKey: string;
        label: string;
        layout: string;
        /** 非空 = 组只对点名设备开放 (新设备需 schemaBindings.deviceSchemas 绑定) */
        deviceKeys: string[] | null;
        /** 「按设备挑行」的矩阵组 (2026-10-23, 如 FDM 工艺预设): 向导不铺全部行, 设备建好后在详情里添加 */
        picksRows?: boolean;
        /** 组内 页签 → 分组 结构 JSON 串 (只在组有布局时下发); 向导按它分组展示, 与设备详情一致 */
        fieldLayout?: string | null;
        fields: Record<string, unknown>[];
    }[];
    mmModes: { value: string; groupCode: string }[];
    /** 本框架已建的参数模板; 向导第 ⑤ 步「从参照设备复制模板」的候选 */
    paramSchemas: {
        id: number;
        archCode: string;
        /** 模板所属设备; 空 = 存量未拆分模板 (按 family) */
        deviceKey: string;
        family: string;
        groupCode: string;
        processType: string;
        deviceKeys?: string[];
        fields: string;
        /** 分组布局 JSON 串 (2026-10-23); null = 未启用分组 */
        layout?: string | null;
    }[];
}

/** 材料套件 preflight: 二级类别 (含编码前缀)/适用理化组/能力位/字典候选/框架材料键粒度. */
export interface MaterialBundlePreflight {
    type1: string;
    type2: string;
    cat2Options: { categoryCode: string; nameEn: string; nameZh: string; codePrefix: string }[];
    physGroups: {
        groupKey: string;
        label: string;
        layout: string;
        rowSource: string;
        rowLabel: string;
        fields: Record<string, unknown>[];
    }[];
    capTypes: { capKey: string; label: string }[];
    dictCodes: Record<string, string[]>;
    archs: { code: string; label: string; materialKeyKind: string }[];
}

export function getDeviceBundlePreflight(
    archCode: string,
): Promise<DeviceBundlePreflight> {
    return request<DeviceBundlePreflight>(
        `${BASE}/device/preflight?archCode=${encodeURIComponent(archCode)}`,
    );
}

export function getMaterialBundlePreflight(
    type1: string,
    type2 = '',
): Promise<MaterialBundlePreflight> {
    const qs = new URLSearchParams({ type1, type2 });
    return request<MaterialBundlePreflight>(
        `${BASE}/material/preflight?${qs.toString()}`,
    );
}

// ───────────────────────── 材料创建向导 (2026-09-16, 设计 doc/relate-import/07) ─────────────────────────
// 向导的状态就是一份 MaterialBundlePayload: 每步"下一步"用当前载荷跑一次 dryRun,
// 报告按 path 回贴到对应步骤的字段; 最后一次提交走同一个接口, 一个工单一个事务落库.

/** 材料主数据段 (字段与后端 materialSection 对齐, 全部可选除 type1/type2/名称). */
export interface BundleMaterialSection {
    xcode?: string;
    nameZh?: string;
    nameEn?: string;
    name?: Record<string, string>;
    type1: string;
    type2: string;
    image?: string;
    craft1?: string;
    craft2?: string;
    surface1?: string;
    surface2?: string;
    color?: string;
    colorHex?: string;
    target?: string;
    coatingMm?: number;
    thickness?: number;
    gloss?: string;
    traits?: string;
    /** 卷料传送比 (> 0, 最多 5 位小数); 缺省 = 未配置 */
    axisYTransmissionRatioRollMaterial?: number;
    flexibility?: string;
    characteristics?: string[];
    status?: string;
    remark?: string;
    /** 创建后立即发布: 需 material:publish; 审批人可在批准时关闭 (B5) */
    publish?: boolean;
}

/** SKU 段: 库里已有的只按 skuCode 关联 (只追加绑定), 不在库的按新建落库. */
export interface BundleSkuItem {
    skuCode: string;
    productNameZh?: string;
    productNameEn?: string;
    prop?: string;
    cmsId?: number;
    status?: string;
    remark?: string;
    /** 规格类型 (SKU 规格模板编码) + 该类型字段值; prop 为原材料 (raw) 时必选必填 */
    specGroup?: string;
    specParams?: Record<string, unknown>;
    /** 仅前端: true = 从 SKU 库选来的已有条目 (提交时只发 skuCode) */
    linked?: boolean;
}

/** 参数行段: 材料键由套件注入, 只给设备/子工艺/字段值. */
export interface BundleParamItem {
    archCode: string;
    deviceKey: string;
    subProcess?: string;
    filmXcode?: string;
    substrateXcode?: string;
    params: Record<string, unknown>;
    resultTag?: string;
    remark?: string;
}

/** 完整的材料套件载荷 (向导草稿 = 这个对象). */
export interface MaterialBundlePayload {
    material: BundleMaterialSection;
    skus?: BundleSkuItem[];
    capabilities?: string[];
    physValues?: Record<string, Record<string, unknown>>;
    tips?: { tipCodes?: string[]; includeRecommended?: boolean };
    params?: BundleParamItem[];
    /** 随套件新建的理化字段组 (2026-09-29): 不再单独提审, 随整包一个工单落库 */
    newDicts?: { physSchemas?: BundleNewPhysSchema[] };
}

/** 与 POST /v1/phys-schemas 同形; cat1 由服务端取本材料一级类别, fields 是 JSON 字符串. */
export interface BundleNewPhysSchema {
    groupKey: string;
    label: string;
    src?: string;
    /** 空 = 该一级类别全部材料; 非空时须含本材料二级类别, 否则组对本材料不开放 */
    cat2Keys?: string[];
    layout?: string;
    rowSource?: string;
    rowLabel?: string;
    fields: string;
    isEnabled?: boolean;
}

/**
 * 提交材料套件 (dryRun=true 只预检不落库).
 * 部署环境返回 taskCode (一个审批工单); 本地 BYPASS 直接落库返回 created.
 */
export function submitMaterialBundle(
    payload: MaterialBundlePayload,
    opts: { dryRun?: boolean; ackWarnings?: boolean } = {},
): Promise<MsgResult<BundleSubmitResult>> {
    const qs = new URLSearchParams();
    if (opts.dryRun) qs.set('dryRun', 'true');
    if (opts.ackWarnings) qs.set('ackWarnings', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return requestWithMsg<BundleSubmitResult>(`${BASE}/material${suffix}`, {
        method: 'POST',
        body: payload,
    });
}

// ───────────────────────── 设备创建向导 (2026-09-17, 设计 doc/relate-import/07 P2) ─────────────────────────
// 与材料向导同构: 向导状态就是一份 DeviceBundle 载荷, 提交走 POST /v1/bundles/device,
// 一个审批工单、一个事务落全部内容 (设备 + 模式引用 + 字段组值 + 校准曲线 + 模板绑定).

/** 设备主数据段 (字段与后端 deviceSection 对齐). */
export interface BundleDeviceSection {
    deviceKey: string;
    name?: string;
    internalKey?: string;
    family: string;
    archCode: string;
    convertToolKey?: string;
    status?: string;
    remark?: string;
    /** 创建后立即发布: 需 device:publish */
    publish?: boolean;
}

/** 加工模式引用 (mm_device_ref): 本设备支持哪些模式, 别名与速度是设备级差异. */
export interface BundleModeRef {
    modeValue: string;
    enabled?: boolean;
    aliasZh?: string;
    aliasEn?: string;
    speedMin?: number | null;
    speedMax?: number | null;
}

/** 功率标定点: 软件功率百分比 → 实测真实功率. */
export interface BundleCalPoint {
    pct: number;
    realW: number;
}

/** 样板参数模板的键 (2026-11-01): 以它为样板给新设备复制一份 (同加工模式/加工类型, 字段与布局照抄).
 *  按设备建的模板给 deviceKey; 存量未拆分模板给 family. */
export interface BundleParamSchemaKey {
    deviceKey?: string;
    family?: string;
    groupCode?: string;
    processType?: string;
}

export interface BundleSchemaBindings {
    /** 字段组 groupKey: 组只对点名设备开放时, 新设备要在这里绑定才能填该组的值 */
    deviceSchemas?: string[];
    paramSchemas?: BundleParamSchemaKey[];
}

/** 模式能力开关 (device_mode): line/fill/bitmap/cut 各一行, 速度只在这里填一次
 *  (D9 单源: 服务端自动 merge 进 mode_capability 矩阵, 矩阵页与工况页读到同一份). */
export interface BundleModeSwitch {
    mode: string;
    isEnabled: boolean;
    speedMin?: number | null;
    speedMax?: number | null;
    note?: string;
}

/** 热工艺标定 (device_thermal_cal): 仅 thermal 框架; (模式, 材料类型) 唯一, tau/beta > 0. */
export interface BundleThermalCal {
    mode: string;
    materialType: string;
    tau?: number | null;
    beta?: number | null;
}

/** 设备套件内的参数行: deviceKey 由套件注入不用写; 材料键按框架 materialKeyKind 三选一. */
export interface BundleDeviceParamItem {
    xcode?: string;
    filmXcode?: string;
    substrateXcode?: string;
    /** inkjet: 二级类别编码 */
    type2?: string;
    /** inkjet: 一级类别编码, 与 type2 成对 */
    type1?: string;
    subProcess?: string;
    params: Record<string, unknown>;
    resultTag?: string;
    remark?: string;
    /** 仅前端 (向导草稿): 行级"字段来源模板"键 deviceKey|family|group|processType;
     *  toRequest 不发本键, 而是把它转成 schemaBindings.paramSchemas (审批通过时给新设备复制这张模板) */
    fieldsFrom?: string;
    /** 仅前端: 这一行的字段怎么来 —— template=选一张已有模板 / custom=自己定加工类型与模式 */
    fieldMode?: 'template' | 'custom';
}

/** 完整的设备套件载荷 (向导草稿 = 这个对象). 段与后端 DeviceBundle 一一对应 (2026-09-22 起向导覆盖全部段). */
export interface DeviceBundlePayload {
    device: BundleDeviceSection;
    modeRefs?: BundleModeRef[];
    modeSwitches?: BundleModeSwitch[];
    /** 字段组值: groupKey → {字段: 值}; 矩阵组是 {行: {字段: 值}} */
    extValues?: Record<string, Record<string, unknown>>;
    calibration?: BundleCalPoint[];
    thermalCal?: BundleThermalCal[];
    schemaBindings?: BundleSchemaBindings;
    tips?: { tipCodes?: string[] };
    params?: BundleDeviceParamItem[];
    /** 随套件新建的前置字典 (2026-09-29): 不再单独提审, 随整包一个工单落库 */
    newDicts?: BundleNewDicts;
}

/**
 * BundleNewDicts 随设备套件新建的模式组 / 模式值 / 本框架设备字段组.
 * 形态与各自的新建接口请求体一致; 预检视同已存在, 审批通过后与设备同一事务落库.
 */
export interface BundleNewDicts {
    modeGroups?: BundleNewModeGroup[];
    modes?: BundleNewMode[];
    deviceSchemas?: BundleNewDeviceSchema[];
}

export interface BundleNewModeGroup {
    code: string;
    nameZh?: string;
    nameEn?: string;
    /** 适用工艺框架 (process_arch.code); 空 = 不限 */
    archCodes?: string[];
    isEnabled?: boolean;
}

export interface BundleNewMode {
    value: string;
    nameZh?: string;
    nameEn?: string;
    groupCode: string;
    isEnabled?: boolean;
}

/** 与 POST /v1/device-schemas 同形; archCode 由服务端取本设备框架, fields 是 JSON 字符串. */
export interface BundleNewDeviceSchema {
    groupKey: string;
    label: string;
    src?: string;
    deviceKeys?: string[];
    layout?: string;
    rowSource?: string;
    rowLabel?: string;
    fields: string;
    /** 组内 页签 → 分组 结构 JSON 串 (可选, 字段用 tab/sec 挂进去) */
    fieldLayout?: string;
    isEnabled?: boolean;
}

/** 提交设备套件 (dryRun=true 只预检不落库). */
export function submitDeviceBundle(
    payload: DeviceBundlePayload,
    opts: { dryRun?: boolean; ackWarnings?: boolean } = {},
): Promise<MsgResult<BundleSubmitResult>> {
    const qs = new URLSearchParams();
    if (opts.dryRun) qs.set('dryRun', 'true');
    if (opts.ackWarnings) qs.set('ackWarnings', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return requestWithMsg<BundleSubmitResult>(`${BASE}/device${suffix}`, {
        method: 'POST',
        body: payload,
    });
}
